import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Pencil,
  Ticket,
  Calendar,
  CheckCircle,
  X,
  Tag,
  Loader2,
  Building2,
  Filter,
  AlertCircle,
} from "lucide-react";
import SummaryApi from "../common";

const CouponManagement = () => {
  const [coupons, setCoupons] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isEditMode, setIsEditMode] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const [filterOrgId, setFilterOrgId] = useState("all");

  const [formData, setFormData] = useState({
    coupon_code: "",
    coupon_title: "",
    discount: "",
    no_of_coupons: "",
    from_date: "",
    to_date: "",
    is_active: 1,
    organization_id: 0,
  });

  // BRAND COLORS
  const BRAND_PURPLE = "#823292";
  const BRAND_PINK = "#f8d7e8";

  const formatForInput = (dateStr) => {
    if (!dateStr) return "";
    return dateStr.split("T")[0];
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const couponRes = await fetch(SummaryApi.coupon_admin_retrieve_all.url);
      const couponData = await couponRes.json();
      if (couponData.status) setCoupons(couponData.data || []);

      const orgRes = await fetch(SummaryApi.getActiveOrganization.url);
      const orgData = await orgRes.json();
      if (orgData.status) setOrganizations(orgData.data || []);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredCoupons = useMemo(() => {
    if (filterOrgId === "all") return coupons;
    return coupons.filter(
      (coupon) => String(coupon.organization_id) === String(filterOrgId),
    );
  }, [coupons, filterOrgId]);

  const getOrgName = (id) => {
    if (!id || id === 0 || id === "0") return "General / Public";
    const org = organizations.find(
      (o) => String(o.organization_id) === String(id),
    );
    return org ? org.organization_name : `Org ID: ${id}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const handleEditClick = (coupon) => {
    setIsEditMode(true);
    setEditingId(coupon.coupon_code_id);
    setFormData({
      coupon_code: coupon.coupon_code,
      coupon_title: coupon.coupon_title,
      discount: coupon.discount,
      no_of_coupons: coupon.no_of_coupons,
      from_date: formatForInput(coupon.from_date),
      to_date: formatForInput(coupon.to_date),
      is_active: coupon.is_active,
      organization_id: coupon.organization_id,
    });
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingId(null);
    setFormData({
      coupon_code: "",
      coupon_title: "",
      discount: "",
      no_of_coupons: "",
      from_date: "",
      to_date: "",
      is_active: 1,
      organization_id: 0,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (checked ? 1 : 0) : value,
    }));
  };

  // Helper for toggle switch
  const handleToggle = () => {
    setFormData((prev) => ({
      ...prev,
      is_active: prev.is_active === 1 ? 0 : 1,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const api = isEditMode
      ? SummaryApi.coupon_update
      : SummaryApi.coupon_create;
    const payload = isEditMode
      ? { ...formData, coupon_code_id: editingId }
      : formData;

    try {
      const response = await fetch(api.url, {
        method: api.method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const result = await response.json();

      if (result.status || result.success) {
        closeModal();
        fetchData();
      } else {
        alert(result.message || "Operation failed");
      }
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-6 bg-[#fdf2f8]/30 min-h-screen font-sans">
      {/* --- HEADER --- */}
      <div className="flex justify-between items-end mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            Coupon Management
          </h1>
          <p className="text-slate-500 text-sm italic font-medium">
            Create and track your promotional offers
          </p>
        </div>
        <button
          onClick={() => {
            setIsEditMode(false);
            setIsModalOpen(true);
          }}
          className="flex items-center gap-2 bg-[#823292] hover:bg-[#6d287a] text-white px-6 py-2.5 rounded-xl font-bold shadow-lg shadow-purple-100 transition-all active:scale-95"
        >
          <Plus size={18} />
          Add New Coupon
        </button>
      </div>

      {/* --- STATS --- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-pink-50 rounded-xl text-[#823292]">
            <Ticket size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Total Coupons
            </p>
            <p className="text-2xl font-black text-slate-800">
              {coupons.length.toString().padStart(2, "0")}
            </p>
          </div>
        </div>
        <div className="bg-white p-6 rounded-2xl border border-pink-100 shadow-sm flex items-center gap-4">
          <div className="p-3 bg-green-50 rounded-xl text-green-600">
            <CheckCircle size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
              Active Now
            </p>
            <p className="text-2xl font-black text-green-600">
              {coupons
                .filter((c) => c.is_active === 1)
                .length.toString()
                .padStart(2, "0")}
            </p>
          </div>
        </div>
      </div>

      {/* --- FILTER BAR --- */}
      <div className="mb-4 flex items-center justify-between bg-white p-4 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4 flex-1">
          <div className="flex items-center gap-2 text-[#823292] font-bold text-xs uppercase tracking-widest">
            <Filter size={14} /> Filter By:
          </div>
          <select
            value={filterOrgId}
            onChange={(e) => setFilterOrgId(e.target.value)}
            className="px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold text-slate-600 focus:ring-2 focus:ring-[#823292]/20 outline-none cursor-pointer min-w-[250px]"
          >
            <option value="all">All Organizations / Types</option>
            <option value="0">General (Public Coupons)</option>
            {organizations.map((org) => (
              <option key={org.organization_id} value={org.organization_id}>
                {org.organization_name}
              </option>
            ))}
          </select>
        </div>
        <div className="text-xs text-slate-400 font-bold">
          Showing {filteredCoupons.length} of {coupons.length} coupons
        </div>
      </div>

      {/* --- DATA TABLE --- */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden min-h-[400px]">
        {isLoading ? (
          <div className="p-20 flex flex-col items-center justify-center text-[#823292]/40">
            <Loader2 className="animate-spin mb-2" size={32} />
            <p className="font-bold uppercase text-[10px] tracking-widest">
              Refreshing...
            </p>
          </div>
        ) : (
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  Coupon Details
                </th>
                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                  Org Assignment
                </th>
                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                  Discount
                </th>
                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                  Validity Period
                </th>
                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">
                  Status
                </th>
                <th className="px-6 py-4 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredCoupons.map((coupon) => (
                <tr
                  key={coupon.coupon_code_id}
                  className="hover:bg-pink-50/20 transition-colors group"
                >
                  <td className="px-6 py-5">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-pink-50 rounded-lg flex items-center justify-center text-[#823292] group-hover:bg-[#823292] group-hover:text-white transition-all">
                        <Tag size={20} />
                      </div>
                      <div>
                        <div className="font-bold text-slate-800">
                          {coupon.coupon_code}
                        </div>
                        <div className="text-xs text-slate-400 italic">
                          {coupon.coupon_title}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span
                      className={`text-sm font-bold ${coupon.organization_id === 0 ? "text-slate-300 italic font-normal" : "text-slate-600"}`}
                    >
                      {getOrgName(coupon.organization_id)}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="font-black text-[#823292] text-lg leading-none">
                      {coupon.discount}%{" "}
                      <span className="text-[10px] uppercase align-middle">
                        Off
                      </span>
                    </div>
                      <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter mt-1">
                      Remaining: {coupon.balance_coupons}/{coupon.no_of_coupons}
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <div className="inline-flex items-center gap-2 text-[11px] text-slate-500 font-bold bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-100">
                      <span>{formatDate(coupon.from_date)}</span>
                      <span className="text-slate-300">→</span>
                      <span>{formatDate(coupon.to_date)}</span>
                    </div>
                  </td>
                  <td className="px-6 py-5 text-center">
                    <span
                      className={`px-3 py-1 text-[10px] font-black uppercase rounded-full tracking-tighter ${
                        coupon.is_active === 1
                          ? "bg-green-100 text-green-600"
                          : "bg-slate-100 text-slate-400"
                      }`}
                    >
                      {coupon.is_active === 1 ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-6 py-5 text-right">
                    <button
                      onClick={() => handleEditClick(coupon)}
                      className="inline-flex items-center gap-1.5 text-[#823292] hover:text-[#6d287a] font-bold text-sm transition-colors"
                    >
                      <Pencil size={14} /> Edit
                    </button>
                  </td>
                </tr>
              ))}
              {filteredCoupons.length === 0 && (
                <tr>
                  <td
                    colSpan="6"
                    className="px-6 py-20 text-center text-slate-400"
                  >
                    <AlertCircle
                      size={40}
                      className="mx-auto mb-2 opacity-10"
                    />
                    <p className="italic font-medium">
                      No coupons found for this criteria.
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* --- CREATE/EDIT MODAL --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"
            onClick={() => !isSubmitting && closeModal()}
          ></div>

          <div className="relative bg-white w-full max-w-xl rounded-3xl shadow-2xl overflow-hidden animate-in zoom-in duration-200 border border-pink-100">
            <div className="px-8 py-6 border-b border-pink-50 flex justify-between items-center bg-pink-50/30">
              <h2 className="text-xl font-bold text-[#823292]">
                {isEditMode ? "Update Existing Offer" : "Launch New Offer"}
              </h2>
              <button
                onClick={closeModal}
                className="p-2 hover:bg-white rounded-full text-slate-400 transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit}
              className="p-8 grid grid-cols-2 gap-5"
            >
              <div className="col-span-2 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase flex items-center gap-1">
                  <Building2 size={12} /> Target Organization
                </label>
                <select
                  disabled={isEditMode}
                  name="organization_id"
                  value={formData.organization_id}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2.5 border rounded-xl outline-none font-bold transition-all ${
                    isEditMode
                      ? "bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed shadow-inner"
                      : "bg-slate-50 border-slate-200 text-slate-700 cursor-pointer focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292]"
                  }`}
                >
                  <option value="0">General (Open To Public)</option>
                  {organizations.map((org) => (
                    <option
                      key={org.organization_id}
                      value={org.organization_id}
                    >
                      {org.organization_name}
                    </option>
                  ))}
                </select>
                {isEditMode && (
                  <p className="text-[10px] text-slate-400 font-bold">
                    * Organization cannot be changed after creation
                  </p>
                )}
              </div>

              <div className="col-span-1 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Coupon Code
                </label>
                <input
                  required
                  name="coupon_code"
                  value={formData.coupon_code}
                  onChange={handleInputChange}
                  type="text"
                  placeholder="SAVE20"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292] outline-none font-bold"
                />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Discount (%)
                </label>
                <input
                  required
                  name="discount"
                  value={formData.discount}
                  onChange={handleInputChange}
                  type="number"
                  placeholder="20"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292] outline-none font-bold"
                />
              </div>
              <div className="col-span-2 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Public Title
                </label>
                <input
                  required
                  name="coupon_title"
                  value={formData.coupon_title}
                  onChange={handleInputChange}
                  type="text"
                  placeholder="Summer Flash Sale"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292] outline-none font-bold"
                />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Start Date
                </label>
                <input
                  required
                  name="from_date"
                  value={formData.from_date}
                  onChange={handleInputChange}
                  type="date"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292] outline-none font-bold"
                />
              </div>
              <div className="col-span-1 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Expiry Date
                </label>
                <input
                  required
                  name="to_date"
                  value={formData.to_date}
                  onChange={handleInputChange}
                  type="date"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292] outline-none font-bold"
                />
              </div>

              <div className="col-span-1 space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Total Issuance
                </label>
                <input
                  required
                  name="no_of_coupons"
                  value={formData.no_of_coupons}
                  onChange={handleInputChange}
                  type="number"
                  placeholder="1000"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-[#823292]/20 focus:border-[#823292] outline-none font-bold"
                />
              </div>

              {/* TOGGLE SWITCH FOR IS_ACTIVE */}
              <div className="col-span-1 flex flex-col justify-end space-y-1.5">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                  Coupon Status
                </label>
                <div className="flex items-center gap-3 h-[46px] bg-slate-50 px-4 rounded-xl border border-slate-200">
                  <span
                    className={`text-[10px] font-black uppercase transition-colors ${formData.is_active === 1 ? "text-[#823292]" : "text-slate-300"}`}
                  >
                    {formData.is_active === 1 ? "Active" : "Offline"}
                  </span>
                  <button
                    type="button"
                    onClick={handleToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-all outline-none ${
                      formData.is_active === 1
                        ? "bg-[#823292]"
                        : "bg-slate-300 shadow-inner"
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ease-in-out ${
                        formData.is_active === 1
                          ? "translate-x-6"
                          : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              </div>

              <div className="col-span-2 mt-6 pt-6 border-t border-slate-100 flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 py-3.5 text-slate-500 font-black uppercase text-xs tracking-widest hover:bg-slate-50 rounded-2xl transition-all"
                >
                  Discard
                </button>
                <button
                  disabled={isSubmitting}
                  type="submit"
                  className="flex-2 grow-[2] py-3.5 bg-[#823292] text-white font-black uppercase text-xs tracking-widest rounded-2xl shadow-xl shadow-purple-100 hover:bg-[#6d287a] transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : isEditMode ? (
                    "Update Coupon"
                  ) : (
                    "Create Offer"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CouponManagement;
